import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';

export interface AppTheme {
  id: string;
  /** Translation key of its name */
  label: string;
  /** Colour shown in the theme picker */
  swatch: string;
}

/**
 * Available themes. The visual definition of each id lives in
 * `src/styles/themes/themes.less` as a `[data-theme='<id>']` block of token overrides.
 * To add a theme: add its block there and register it here.
 */
export const APP_THEMES: AppTheme[] = [
  // Ordered by palette: warm to cool hues, then neutrals
  { id: 'sunset', label: 'themes.sunset', swatch: '#e64a19' },
  { id: 'orange', label: 'themes.orange', swatch: '#f57c00' },
  { id: 'sunny', label: 'themes.sunny', swatch: '#fbbf24' },
  { id: 'forest', label: 'themes.forest', swatch: '#2e7d32' },
  { id: 'ocean', label: 'themes.ocean', swatch: '#0277bd' },
  { id: 'indigo', label: 'themes.indigo', swatch: '#3f51b5' },
  { id: 'coffee', label: 'themes.coffee', swatch: '#6d4c41' },
  { id: 'gray', label: 'themes.gray', swatch: '#607d8b' },
  { id: 'dark', label: 'themes.dark', swatch: '#1b1e2e' },
];

export const DEFAULT_THEME = 'indigo';

/**
 * Cache of the signed-in user's theme, used only to avoid a flash of the
 * default theme while the saved one loads after a page reload. The database is
 * the source of truth; the cache is cleared on logout.
 */
const CACHE_KEY = 'app-theme';

/**
 * Applies the active theme and keeps it per user:
 * - logged out (login, register, password reset): always the default theme
 * - on login: the theme saved on the user's account (MongoDB) is loaded
 * - on change: shown at once by the profile form (`preview`), and kept once it is saved to the account (`keep`)
 * - tried out (the style guide): `preview`, then `endPreview` puts the user's own back
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private readonly settingsUrl = '/api/auth/me/settings';

  readonly themes = APP_THEMES;
  readonly current = signal<string>(DEFAULT_THEME);

  /** Set when the user picks a theme, so a slower server response doesn't overwrite it */
  private pickedThisSession = false;
  /** The signed-in user's own theme, as last known: what `endPreview` puts back */
  private own = DEFAULT_THEME;
  /** A theme is shown that isn't the user's own: a late answer of the server doesn't replace it */
  private previewing = false;
  /** Whose themes these are: undefined until the first answer, null signed out */
  private userId: string | null | undefined = undefined;

  constructor() {
    this.auth.currentUser$.subscribe((user) => {
      // A renewed token is the same user again: their theme is already on (and may be previewed)
      const id = user ? (user.userId ?? '') : null;
      if (id === this.userId) return;
      this.userId = id;
      if (user) this.onLogin();
      else this.onLogout();
    });
  }

  /**
   * Makes a theme the user's own, once the server has saved it (the profile form saves it
   * together with the rest, see `ProfileService.updateDetails`): it is shown and kept for the next visit.
   */
  keep(id: string): void {
    if (!this.isKnown(id)) return;
    this.pickedThisSession = true;
    this.previewing = false;
    this.own = id;
    this.apply(id);
    this.writeCache(id);
  }

  /** Shows a theme without saving it, e.g. while the profile form is open. Use `keep` once it is saved. */
  preview(id: string): void {
    if (!this.isKnown(id)) return;
    this.previewing = true;
    this.apply(id);
  }

  /** The preview is over: the user's own theme again; nothing once they have signed out (the default is on) */
  endPreview(): void {
    this.previewing = false;
    if (this.userId) this.apply(this.own);
  }

  private onLogin(): void {
    this.pickedThisSession = false;
    this.previewing = false;

    const cached = this.readCache();
    if (cached) {
      this.own = cached;
      this.apply(cached);
    }

    this.http.get<{ theme: string }>(this.settingsUrl).subscribe({
      next: ({ theme }) => {
        if (this.pickedThisSession) return;
        const id = this.isKnown(theme) ? theme : DEFAULT_THEME;
        this.own = id;
        this.writeCache(id);
        if (!this.previewing) this.apply(id);
      },
      error: (err) => console.error('Could not load theme', err),
    });
  }

  private onLogout(): void {
    this.previewing = false;
    this.own = DEFAULT_THEME;
    this.clearCache();
    this.apply(DEFAULT_THEME);
  }

  private isKnown(id: string | null | undefined): id is string {
    return !!id && this.themes.some((t) => t.id === id);
  }

  private apply(id: string): void {
    this.current.set(id);
    document.documentElement.setAttribute('data-theme', id);
  }

  private readCache(): string | null {
    try {
      const saved = localStorage.getItem(CACHE_KEY);
      return this.isKnown(saved) ? saved : null;
    } catch {
      return null;
    }
  }

  private writeCache(id: string): void {
    try {
      localStorage.setItem(CACHE_KEY, id);
    } catch {
      // Storage can be unavailable (private mode); the saved account theme still applies
    }
  }

  private clearCache(): void {
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch {
      // ignore
    }
  }
}
